'use client';

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import type { BookingBlock } from '@shared/types/salespads';
import type { EventData } from '@/lib/fetch-event-data';
import { pushDataLayer } from '@/lib/track-gtm';
import { BookingModalPopup } from './BookingModalPopup';

type ButtonConfig = NonNullable<BookingBlock['data']['button']>;

const sizeMap: Record<string, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
};
const alignMap: Record<string, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

interface Props {
  button: ButtonConfig;
  eventData: EventData;
  blockId: string;
}

/** The button shown in modal display mode + the popup it opens. Mirrors the
 * ButtonModalBlock button styling/tracking, but opens the polished BookingModalPopup. */
export function BookingModalTrigger({ button, eventData, blockId }: Props) {
  const [open, setOpen] = useState(false);

  // Preload the BookingWidget chunk shortly after mount so the popup opens fast.
  useEffect(() => {
    const t = setTimeout(() => { import('../booking/BookingWidget').catch(() => {}); }, 1000);
    return () => clearTimeout(t);
  }, []);

  const size = sizeMap[button.size] || sizeMap.md;
  const align = alignMap[button.align] || alignMap.center;
  const buttonColor = button.color || '#4f46e5';
  const isGradient = buttonColor.includes('gradient');

  let className = `inline-block rounded-lg font-semibold transition-all cursor-pointer ${size}`;
  const style: CSSProperties = {};
  if (button.style === 'primary') {
    className += ' text-white hover:opacity-90';
    style.background = buttonColor;
  } else if (button.style === 'secondary') {
    className += ' bg-gray-100 text-gray-900 hover:bg-gray-200';
  } else if (isGradient) {
    // Gradient outline: gradient border + gradient text (matches ButtonModalBlock).
    className += ' border-2 bg-transparent hover:opacity-80';
    style.borderImage = `${buttonColor} 1`;
    style.background = buttonColor;
    style.backgroundClip = 'text';
    style.WebkitBackgroundClip = 'text';
    style.color = 'transparent';
  } else {
    className += ' border-2 bg-transparent hover:opacity-80';
    style.borderColor = buttonColor;
    style.color = buttonColor;
  }
  if (button.textColor) style.color = button.textColor;
  if (button.fullWidth) className += ' w-full text-center';

  return (
    <>
      <div className={`flex ${align} py-2`}>
        <button
          type="button"
          className={className}
          style={style}
          onClick={() => {
            pushDataLayer('salescal.salespad_click', {
              blockId,
              blockType: 'booking-modal',
              label: button.text,
              url: '',
              clickAction: 'modal',
            });
            setOpen(true);
          }}
        >
          {button.text}
        </button>
      </div>
      <BookingModalPopup eventData={eventData} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
